import type { RefObject, MutableRefObject } from 'react'
import type { Obstacle, GamePhase, UiState, GameState } from './types'
import { drawBackground } from './background/scenery'

const drawObstacle = (
  ctx: CanvasRenderingContext2D,
  o: Obstacle,
  groundY: number,
  elapsed: number,
) => {
  const offset = o.offsetY ?? 0
  const y = groundY - o.h - offset

  if (o.type === 'cone') {
    const baseY = y + o.h
    const midX = o.x + o.w / 2

    ctx.fillStyle = '#8c4328'
    ctx.beginPath()
    ctx.moveTo(o.x, baseY)
    ctx.lineTo(midX, y)
    ctx.lineTo(o.x + o.w, baseY)
    ctx.closePath()
    ctx.fill()

    ctx.fillStyle = 'rgba(255,255,255,0.45)'
    const bandY = y + o.h * 0.55
    ctx.fillRect(o.x + o.w * 0.18, bandY, o.w * 0.64, Math.max(2, o.h * 0.14))

    ctx.fillStyle = 'rgba(0,0,0,0.25)'
    ctx.fillRect(o.x - 2, baseY - 3, o.w + 4, 3)
    return
  }

  if (o.type === 'barrier') {
    const baseY = y + o.h
    const plankY = y + 4
    const plankH = Math.max(8, o.h - 10)

    ctx.fillStyle = '#3d434f'
    ctx.fillRect(o.x, plankY, o.w, plankH)

    ctx.save()
    ctx.beginPath()
    ctx.rect(o.x, plankY, o.w, plankH)
    ctx.clip()
    ctx.fillStyle = 'rgba(255,255,255,0.25)'
    for (let i = -o.h; i < o.w + o.h; i += 10) {
      ctx.beginPath()
      ctx.moveTo(o.x + i, plankY)
      ctx.lineTo(o.x + i + 6, plankY)
      ctx.lineTo(o.x + i + 16, plankY + plankH)
      ctx.lineTo(o.x + i + 10, plankY + plankH)
      ctx.closePath()
      ctx.fill()
    }
    ctx.restore()

    ctx.fillStyle = '#232731'
    ctx.fillRect(o.x + 4, baseY - 6, 6, 6)
    ctx.fillRect(o.x + o.w - 10, baseY - 6, 6, 6)

    ctx.fillStyle = 'rgba(0,0,0,0.2)'
    ctx.fillRect(o.x, baseY - 1, o.w, 2)
    return
  }

  const pivotX = o.x + o.w / 2
  const pivotY = y + o.h / 2
  const angle = (elapsed * 3.2 + (o.spinOffset ?? 0)) % (Math.PI * 2)
  const scale = 0.4

  ctx.save()
  ctx.translate(pivotX, pivotY)
  ctx.rotate(angle)
  ctx.scale(scale, scale)

  ctx.fillStyle = '#6d4c41'
  ctx.fillRect(-2, -32, 4, 32)

  ctx.fillStyle = '#3e2723'
  ctx.beginPath()
  ctx.ellipse(0, -38, 12, 8, 0, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#bca08c'
  ctx.ellipse(0, -38, 7, 4, 0, 0, Math.PI * 2)
  ctx.fill()

  ctx.restore()
}

/**
 * Jolie F1 simple, "emoji racecar vibe" (sans copier).
 * Orientation: vers la droite (sens du jeu).
 */
const drawF1 = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
) => {
  // Pixel-art renderer: draw on a small grid then scale up.
  // Car faces RIGHT (nose on the right, rear wing on the left).

  const GW = 34 // grid width
  const GH = 16 // grid height

  // Keep pixels crisp
  ctx.save()
  const prevSmoothing = ctx.imageSmoothingEnabled
  ctx.imageSmoothingEnabled = false

  // Fit grid into (w,h) with uniform scale
  const s = Math.max(1, Math.floor(Math.min(w / GW, h / GH) * 2))
  const ox = x + Math.floor((w - GW * s) / 2)
  const oy = y + Math.floor((h - GH * s) / 2)

  const px = (gx: number, gy: number, color: string) => {
    ctx.fillStyle = color
    ctx.fillRect(ox + gx * s, oy + gy * s, s, s)
  }
  const rect = (gx: number, gy: number, gw: number, gh: number, color: string) => {
    ctx.fillStyle = color
    ctx.fillRect(ox + gx * s, oy + gy * s, gw * s, gh * s)
  }

  // Palette (emoji-ish)
  const BLACK = '#1a1a1a'
  const DARK = '#2b2b2b'
  const RED = '#e53935'
  const RED_D = '#c62828'
  const RED_D2 = '#a61f1f'
  const WHITE = '#f3f3f3'
  const BLUE = '#1e88e5'
  const GLASS = 'rgba(255,255,255,0.35)'
  const SHADOW = 'rgba(0,0,0,0.22)'

  // Shadow under car
  ctx.fillStyle = SHADOW
  ctx.fillRect(ox + 5 * s, oy + 14 * s, 24 * s, 2 * s)

  // Wheels (pixel circles-ish)
  // rear wheel (left)
  rect(6, 11, 6, 4, BLACK)
  rect(7, 10, 4, 6, BLACK)
  rect(8, 12, 2, 2, DARK) // rim center

  // front wheel (right)
  rect(24, 11, 6, 4, BLACK)
  rect(25, 10, 4, 6, BLACK)
  rect(26, 12, 2, 2, DARK)

  // Rear wing (left)
  rect(2, 6, 7, 2, RED_D2)
  rect(3, 4, 5, 2, RED_D2)
  rect(4, 6, 2, 4, RED_D2)

  // Main body base (red)
  rect(8, 8, 16, 4, RED)         // mid body
  rect(10, 6, 12, 2, RED)        // upper body
  rect(11, 5, 9, 1, RED)         // top ridge
  rect(9, 12, 14, 1, RED)        // lower edge

  // Sidepod shading (gives depth)
  rect(10, 9, 12, 2, RED_D)
  rect(12, 10, 8, 1, RED_D)

  // Nose / front cone (right)
  rect(24, 8, 6, 3, RED)         // nose base
  rect(28, 9, 4, 2, RED)         // tip block
  px(32, 10, RED)                // tip pixel
  px(31, 8, RED)                 // top pixel
  px(31, 11, RED)                // bottom pixel

  // Front wing (right)
  rect(26, 11, 8, 2, RED_D2)
  rect(27, 10, 6, 1, RED_D2)

  // Cockpit (dark bubble)
  rect(15, 6, 5, 3, BLACK)
  px(14, 7, BLACK)
  px(20, 7, BLACK)

  // Driver helmet (white) + visor (blue)
  rect(16, 6, 2, 2, WHITE)
  px(15, 6, WHITE)
  px(18, 6, WHITE)
  px(16, 8, WHITE)

  rect(16, 7, 2, 1, BLUE)
  px(17, 6, GLASS) // tiny highlight

  // Small white accent sticker on body (emoji vibe)
  rect(12, 9, 3, 1, 'rgba(255,255,255,0.55)')

  // Little outline accents (helps readability)
  // underbody line
  rect(9, 13, 14, 1, 'rgba(0,0,0,0.18)')
  // cockpit highlight
  px(16, 6, 'rgba(255,255,255,0.18)')

  ctx.imageSmoothingEnabled = prevSmoothing
  ctx.restore()
}


const drawUiPanel = (
  ctx: CanvasRenderingContext2D,
  title: string,
  subtitle: string,
  cta: string,
  uiRef: RefObject<UiState>,
  width: number,
  height: number,
) => {
  ctx.fillStyle = 'rgba(255,255,255,0.85)'
  const panelWidth = Math.min(280, width - 40)
  const panelX = (width - panelWidth) / 2
  const panelY = height / 2 - 70
  ctx.fillRect(panelX, panelY, panelWidth, 120)

  ctx.fillStyle = 'rgba(0,0,0,0.8)'
  ctx.font = 'bold 16px system-ui, -apple-system, Segoe UI, Roboto, Arial'
  ctx.fillText(title, panelX + 14, panelY + 30)

  ctx.font = '13px system-ui, -apple-system, Segoe UI, Roboto, Arial'
  ctx.fillText(subtitle, panelX + 14, panelY + 54)

  const btnWidth = 120
  const btnHeight = 36
  const btnX = panelX + (panelWidth - btnWidth) / 2
  const btnY = panelY + 66

  ctx.fillStyle = 'rgba(0,0,0,0.85)'
  ctx.fillRect(btnX, btnY, btnWidth, btnHeight)
  ctx.fillStyle = 'white'
  ctx.font = 'bold 14px system-ui, -apple-system, Segoe UI, Roboto, Arial'
  ctx.fillText(cta, btnX + 30, btnY + 23)

  const currentRef = uiRef.current
  if (currentRef) {
    currentRef.startBtn = { x: btnX, y: btnY, w: btnWidth, h: btnHeight }
  }
}

const renderScene = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  phase: GamePhase,
  uiRef: RefObject<UiState>,
  stateRef: MutableRefObject<GameState>,
) => {
  const s = stateRef.current
  ctx.clearRect(0, 0, width, height)

  if (s.scenery) {
    drawBackground(ctx, s.scenery, width, height, s.score)
  } else {
    const fallbackSky = ctx.createLinearGradient(0, 0, 0, height)
    fallbackSky.addColorStop(0, '#e3ecff')
    fallbackSky.addColorStop(1, '#c2d0ec')
    ctx.fillStyle = fallbackSky
    ctx.fillRect(0, 0, width, height)
    ctx.fillStyle = '#f2efe4'
    ctx.fillRect(0, height * 0.82, width, height * 0.2)
  }

  ctx.fillStyle = '#fdfdfd'
  const scroll = (s.score * 3) % 28
  for (let i = 0; i < 18; i += 1) {
    const x = (i * 28 + scroll) % (width + 28) - 28
    ctx.fillRect(x, s.groundY + 6, 14, 2)
  }

  s.obstacles.forEach((o) => drawObstacle(ctx, o, s.groundY, s.elapsed))

  const c = s.car
  drawF1(ctx, c.x, c.y, c.w, c.h)

  ctx.fillStyle = 'rgba(0,0,0,0.65)'
  ctx.font = '12px system-ui, -apple-system, Segoe UI, Roboto, Arial'
  ctx.fillText(`Score: ${Math.floor(s.score)}`, 10, 16)
  ctx.fillText(`Best: ${Math.floor(s.best)}`, 10, 32)

  if (phase === 'ready') {
    drawUiPanel(ctx, 'Infinite Car Runner', 'Entrée/Espace/Clic pour démarrer', 'START', uiRef, width, height)
  } else if (phase === 'crashed') {
    drawUiPanel(
      ctx,
      'Crash 💥',
      `Score ${Math.floor(s.lastScoreAtCrash)} • Best ${Math.floor(s.best)}`,
      'RESTART',
      uiRef,
      width,
      height,
    )
  } else {
    ctx.fillStyle = 'rgba(0,0,0,0.45)'
    ctx.fillText('Espace / clic : sauter', width - 138, 16)
  }
}

export { drawObstacle, drawF1, drawUiPanel, renderScene }
